import { ArrowLeft, ArrowRight, Clock, Loader, Mail, MapPin, Package, PackageCheck, Phone, Truck, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';
import './EntregasRecogidas.css';

const EntregasRecogidas = () => {
  const { authenticatedFetch } = useAuth();
  const dateInputRef = useRef(null);
  const [pickups, setPickups] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Por defecto, fecha de hoy en formato YYYY-MM-DD
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // Cargar entregas y recogidas
  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      params.set('date', selectedDate);

      const url = `${buildApiUrl(`${API_ENDPOINTS.BOOKINGS}/deliveries`)}?${params.toString()}`;
      const response = await authenticatedFetch(url);
      const data = await response.json();

      if (data.success) {
        setPickups(data.data.pickups || []);
        setDeliveries(data.data.deliveries || []);
      } else {
        setError(data.message || 'Error al cargar las entregas y recogidas');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error de conexión al servidor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveries();
  }, [selectedDate]);

  // Navegar entre fechas
  const changeDate = (days, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + days);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  // Ir a hoy
  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today.toISOString().split('T')[0]);
  };

  const openDatePicker = () => {
    const el = dateInputRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        /* Sin showPicker o error de permiso */
      }
    }
    el.focus();
  };

  const handleDateInputClick = () => {
    const el = dateInputRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (typeof el.showPicker === 'function') {
        try {
          el.showPicker();
        } catch {
          el.focus();
        }
      } else {
        el.focus();
      }
    });
  };

  const handleDateInputKeyDown = (e) => {
    if (e.key === 'Tab' || e.key === 'Escape') return;
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(e.key)) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDatePicker();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const blocksTyping =
      /^[0-9]$/.test(e.key) ||
      e.key === '/' ||
      e.key === '-' ||
      e.key === '.' ||
      e.key === 'Backspace' ||
      e.key === 'Delete';
    if (blocksTyping) {
      e.preventDefault();
    }
  };

  // Formatear fecha para mostrar
  const formatDisplayDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const dateStr = date.toDateString();
    const todayStr = today.toDateString();
    const tomorrowStr = tomorrow.toDateString();
    const yesterdayStr = yesterday.toDateString();

    if (dateStr === todayStr) return 'Hoy';
    if (dateStr === tomorrowStr) return 'Mañana';
    if (dateStr === yesterdayStr) return 'Ayer';
    
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Formatear hora
  const formatTimeOnly = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('es-ES', {
        timeZone: 'Europe/Madrid',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formateando hora:', error);
      return 'Hora inválida';
    }
  };

  // Obtener badge de estado
  const getStatusBadge = (status) => {
    const statusMap = {
      'PENDING': { label: 'Pendiente', class: 'pending' },
      'CONFIRMED': { label: 'Confirmada', class: 'confirmed' },
      'CANCELLED': { label: 'Cancelada', class: 'cancelled' },
      'COMPLETED': { label: 'Completada', class: 'completed' }
    };
    
    const statusInfo = statusMap[status] || { label: status, class: 'unknown' };
    
    return (
      <span className={`status-badge ${statusInfo.class}`}>
        {statusInfo.label}
      </span>
    );
  };

  // Verificar si la fecha seleccionada es hoy
  const isToday = () => {
    const today = new Date();
    const selected = new Date(selectedDate);
    return today.toDateString() === selected.toDateString();
  };

  // Construir URL de Google Maps para una dirección
  const getGoogleMapsUrl = (address) => {
    if (!address || address === 'N/A') return null;
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  };

  if (loading && pickups.length === 0 && deliveries.length === 0) {
    return (
      <div className="loading-container">
        <Loader className="loading-spinner" />
        <p>Cargando entregas y recogidas...</p>
      </div>
    );
  }

  return (
    <div className="entregas-recogidas-page">
      <div className="page-header">
        <h1>Entregas y Recogidas</h1>
        <div className="date-selector">
          <button 
            type="button"
            className="date-nav-btn date-nav-btn-prev" 
            onClick={(e) => changeDate(-1, e)}
            title="Día anterior"
            aria-label="Día anterior"
          >
            <ArrowLeft size={24} strokeWidth={2.5} />
          </button>
          <div className="date-display">
            <input
              ref={dateInputRef}
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              onClick={handleDateInputClick}
              onKeyDown={handleDateInputKeyDown}
              onPaste={(e) => e.preventDefault()}
              className="date-input date-input--picker-boost"
              title="Pulse para abrir el calendario"
              autoComplete="off"
              inputMode="none"
            />
            <span className="date-label">{formatDisplayDate(selectedDate)}</span>
            {!isToday() && (
              <button className="today-btn" onClick={goToToday}>
                Ir a hoy
              </button>
            )}
          </div>
          <button 
            type="button"
            className="date-nav-btn date-nav-btn-next" 
            onClick={(e) => changeDate(1, e)}
            title="Día siguiente"
            aria-label="Día siguiente"
          >
            <ArrowRight size={24} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Mensaje de error */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Estadísticas del día */}
      <div className="stats-grid">
        <div className="stat-card delivery">
          <div className="stat-icon">
            <PackageCheck size={24} />
          </div>
          <div className="stat-content">
            <h3>Entregas</h3>
            <p className="stat-number">{deliveries.length}</p>
          </div>
        </div>
        <div className="stat-card pickup">
          <div className="stat-icon">
            <Truck size={24} />
          </div>
          <div className="stat-content">
            <h3>Recogidas</h3>
            <p className="stat-number">{pickups.length}</p>
          </div>
        </div>
        <div className="stat-card total">
          <div className="stat-icon">
            <Package size={24} />
          </div>
          <div className="stat-content">
            <h3>Total</h3>
            <p className="stat-number">{pickups.length + deliveries.length}</p>
          </div>
        </div>
      </div>

      {/* Sección de Entregas */}
      <div className="section-container">
        <div className="section-header delivery-header">
          <PackageCheck size={24} />
          <h2>Entregas del Día ({deliveries.length})</h2>
        </div>
        {deliveries.length === 0 ? (
          <div className="empty-section">
            <p>No hay entregas programadas para este día</p>
          </div>
        ) : (
          <div className="cards-grid">
            {deliveries.map((delivery) => (
              <div key={`delivery-${delivery.id}`} className="delivery-card delivery-card-item">
                <div className="card-header">
                  <div className="card-id">
                    <span className="card-id-number">#{delivery.id}</span>
                    {delivery.bookingReference && (
                      <span className="card-reference">{delivery.bookingReference}</span>
                    )}
                  </div>
                  {getStatusBadge(delivery.status)}
                </div>
                
                <div className="card-time">
                  <Clock size={18} />
                  <span className="time-value">{formatTimeOnly(delivery.time)}</span>
                </div>

                <div className="card-contact">
                  <div className="contact-item">
                    <User size={16} />
                    <strong>{delivery.contact.name}</strong>
                  </div>
                  <div className="contact-item">
                    <Phone size={16} />
                    <a href={`tel:${delivery.contact.fullPhone}`} className="phone-link">
                      {delivery.contact.fullPhone}
                    </a>
                  </div>
                  <div className="contact-item">
                    <Mail size={16} />
                    <span>{delivery.contact.email}</span>
                  </div>
                </div>

                <div className="card-address">
                  <MapPin size={16} />
                  {getGoogleMapsUrl(delivery.address) ? (
                    <a 
                      href={getGoogleMapsUrl(delivery.address)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="address-link"
                    >
                      {delivery.address}
                    </a>
                  ) : (
                    <span>{delivery.address}</span>
                  )}
                </div>

                <div className="card-vehicles">
                  <strong>Vehículos:</strong>
                  <div className="vehicles-list">
                    {delivery.vehicles.map((vehicle, index) => (
                      <span key={index} className="vehicle-badge">
                        {vehicle.brand} {vehicle.model} ({vehicle.size})
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sección de Recogidas */}
      <div className="section-container">
        <div className="section-header pickup-header">
          <Truck size={24} />
          <h2>Recogidas del Día ({pickups.length})</h2>
        </div>
        {pickups.length === 0 ? (
          <div className="empty-section">
            <p>No hay recogidas programadas para este día</p>
          </div>
        ) : (
          <div className="cards-grid">
            {pickups.map((pickup) => (
              <div key={`pickup-${pickup.id}`} className="delivery-card pickup-card">
                <div className="card-header">
                  <div className="card-id">
                    <span className="card-id-number">#{pickup.id}</span>
                    {pickup.bookingReference && (
                      <span className="card-reference">{pickup.bookingReference}</span>
                    )}
                  </div>
                  {getStatusBadge(pickup.status)}
                </div>
                
                <div className="card-time">
                  <Clock size={18} />
                  <span className="time-value">{formatTimeOnly(pickup.time)}</span>
                </div>

                <div className="card-contact">
                  <div className="contact-item">
                    <User size={16} />
                    <strong>{pickup.contact.name}</strong>
                  </div>
                  <div className="contact-item">
                    <Phone size={16} />
                    <a href={`tel:${pickup.contact.fullPhone}`} className="phone-link">
                      {pickup.contact.fullPhone}
                    </a>
                  </div>
                  <div className="contact-item">
                    <Mail size={16} />
                    <span>{pickup.contact.email}</span>
                  </div>
                </div>

                <div className="card-address">
                  <MapPin size={16} />
                  {getGoogleMapsUrl(pickup.address) ? (
                    <a 
                      href={getGoogleMapsUrl(pickup.address)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="address-link"
                    >
                      {pickup.address}
                    </a>
                  ) : (
                    <span>{pickup.address}</span>
                  )}
                </div>

                <div className="card-vehicles">
                  <strong>Vehículos:</strong>
                  <div className="vehicles-list">
                    {pickup.vehicles.map((vehicle, index) => (
                      <span key={index} className="vehicle-badge">
                        {vehicle.brand} {vehicle.model} ({vehicle.size})
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EntregasRecogidas;

